<template>
  <div>
    <div class="py-3"></div>

    <!-- ================= 1단: 플레이스먼트 목록 ================= -->
    <v-card v-if="!selectedPlacement" class="mx-auto" max-width="95%">
      <v-overlay :value="loading" absolute opacity="0.7">
        <v-progress-circular indeterminate size="48" />
      </v-overlay>

      <v-toolbar color="light-blue darken-3" dark>
        <v-toolbar-title>쿠폰 노출(플레이스먼트) 관리</v-toolbar-title>
        <v-spacer />
        <v-btn color="white" outlined :disabled="loading" @click="openCreateDialog">
          <v-icon left>mdi-plus</v-icon>
          새 플레이스먼트
        </v-btn>
      </v-toolbar>

      <v-card-text class="pa-0">
        <v-data-table
          v-if="placements.length > 0"
          :headers="placementHeaders"
          :items="placements"
          :items-per-page="20"
          class="elevation-0 row-pointer"
          hide-default-footer
          @click:row="openPlacement"
        >
          <template v-slot:item.CODE="{ item }">
            <span class="font-weight-medium">{{ item.CODE }}</span>
          </template>
          <template v-slot:item.NAME="{ item }">
            {{ item.NAME || '-' }}
          </template>
          <template v-slot:item.ACTIVE="{ item }">
            <v-chip x-small dark :color="item.ACTIVE === 'Y' ? 'success' : 'grey'">
              {{ item.ACTIVE === 'Y' ? '활성' : '비활성' }}
            </v-chip>
          </template>
          <template v-slot:item.CACHE_VERSION="{ item }">
            <span class="text-body-2">v{{ item.CACHE_VERSION }}</span>
          </template>
          <template v-slot:item.CACHE_GENERATED_AT="{ item }">
            <span class="text-body-2 grey--text text--darken-1">
              {{ formatDate(item.CACHE_GENERATED_AT) }}
            </span>
          </template>
        </v-data-table>

        <v-container v-if="placements.length === 0 && !loading" class="text-center py-8">
          <v-icon size="64" color="grey lighten-1">mdi-view-grid-outline</v-icon>
          <p class="text-body-1 grey--text mt-3">
            등록된 플레이스먼트가 없습니다. '새 플레이스먼트' 버튼으로 추가하세요.
          </p>
        </v-container>
      </v-card-text>
    </v-card>

    <!-- ================= 2단: 플레이스먼트 편집 ================= -->
    <v-card v-else class="mx-auto" max-width="95%">
      <v-overlay :value="loading" absolute opacity="0.7">
        <v-progress-circular indeterminate size="48" />
      </v-overlay>

      <v-toolbar color="light-blue darken-3" dark>
        <v-btn icon @click="backToList">
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <v-toolbar-title>
          {{ selectedPlacement.NAME || selectedPlacement.CODE }}
          <span class="text-body-2 ml-2">({{ selectedPlacement.CODE }})</span>
        </v-toolbar-title>
        <v-spacer />
        <v-chip small color="white" text-color="light-blue darken-3" class="mr-2">
          캐시 v{{ selectedPlacement.CACHE_VERSION }}
        </v-chip>
        <v-btn color="white" outlined :disabled="loading" class="mr-2" @click="openPreview">
          <v-icon left>mdi-cellphone-screenshot</v-icon>
          미리보기
        </v-btn>
        <v-btn color="white" outlined :disabled="loading" @click="reloadDetail">
          <v-icon left>mdi-refresh</v-icon>
          새로고침
        </v-btn>
      </v-toolbar>

      <v-card-text>
        <!-- 안내 -->
        <v-alert dense text type="info" class="mb-4 text-body-2">
          체크박스를 켜고 끄면 <strong>즉시 저장</strong>됩니다. 별도의 저장 버튼은 없으며,
          변경 시 서버가 캐시 버전을 자동으로 올립니다.
        </v-alert>

        <!-- 기본 카드 -->
        <v-card outlined class="mb-4">
          <v-card-title class="text-subtitle-1 py-2">기본</v-card-title>
          <v-divider />
          <v-card-text class="pb-2">
            <v-row dense>
              <v-col cols="12" md="4">
                <v-switch
                  v-model="basic.active"
                  :disabled="basicSaving"
                  label="활성"
                  hide-details
                  class="mt-0"
                  @change="onBasicChange('active', $event)"
                />
                <div class="text-caption grey--text">
                  비활성 시 이 플레이스먼트는 앱에 노출되지 않습니다.
                </div>
              </v-col>
              <v-col cols="12" md="4">
                <v-switch
                  v-model="basic.newCategoryDefault"
                  :disabled="basicSaving"
                  label="새 카테고리 기본노출"
                  hide-details
                  class="mt-0"
                  @change="onBasicChange('newCategoryDefault', $event)"
                />
                <div class="text-caption grey--text">
                  개별 설정이 없는 카테고리에 적용되는 기본값입니다.
                </div>
              </v-col>
              <v-col cols="12" md="4">
                <v-switch
                  v-model="basic.newProductDefault"
                  :disabled="basicSaving"
                  label="새 상품 기본노출"
                  hide-details
                  class="mt-0"
                  @change="onBasicChange('newProductDefault', $event)"
                />
                <div class="text-caption grey--text">
                  개별 설정이 없는 상품에 적용되는 기본값입니다.
                </div>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- 좌우 2단: 좌=카테고리 노출 / 우=상품 노출·정렬(평면 전체) -->
        <v-row>
          <!-- 좌: 카테고리 on/off -->
          <v-col cols="12" md="4">
            <v-card outlined>
              <v-toolbar flat dense color="grey lighten-4">
                <v-toolbar-title class="text-subtitle-2">
                  카테고리 노출 ({{ categories.length }})
                </v-toolbar-title>
              </v-toolbar>
              <v-divider />
              <v-list dense class="py-0 scroll-pane">
                <template v-for="(cat, idx) in categories">
                  <v-list-item :key="'cat-' + cat.PID">
                    <v-list-item-action class="mr-3">
                      <v-checkbox
                        :input-value="cat.EFFECTIVE_ENABLED === 'Y'"
                        :disabled="!!categoryBusy[cat.PID]"
                        hide-details
                        class="mt-0 pt-0"
                        @change="toggleCategory(cat, $event)"
                      />
                    </v-list-item-action>
                    <v-list-item-content>
                      <v-list-item-title class="text-body-2">
                        {{ cat.DISPLAY_NAME || cat.DAOU_CATEGORY }}
                      </v-list-item-title>
                      <v-list-item-subtitle class="text-caption">
                        <span class="grey--text">{{ cat.DAOU_CATEGORY }}</span>
                        <span
                          v-if="cat.OVERRIDE_ENABLED === null || cat.OVERRIDE_ENABLED === undefined"
                          class="text-caption grey--text ml-2"
                        >
                          · 기본값 적용중
                        </span>
                        <span v-else class="text-caption blue--text ml-2">
                          · 개별 설정({{ cat.OVERRIDE_ENABLED === 'Y' ? '노출' : '숨김' }})
                        </span>
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                  <v-divider :key="'cat-div-' + cat.PID" v-if="idx < categories.length - 1" />
                </template>
              </v-list>
              <v-container v-if="categories.length === 0" class="text-center py-6">
                <span class="text-body-2 grey--text">카테고리가 없습니다.</span>
              </v-container>
            </v-card>
          </v-col>

          <!-- 우: 상품 노출·정렬 (평면 전체) -->
          <v-col cols="12" md="8">
            <v-card outlined>
              <v-toolbar flat dense color="grey lighten-4">
                <v-toolbar-title class="text-subtitle-2">
                  상품 노출·정렬 (노출 {{ visibleCount }} / 전체 {{ flatProducts.length }})
                </v-toolbar-title>
                <v-spacer />
                <v-progress-circular
                  v-if="flatLoading"
                  indeterminate
                  size="18"
                  width="2"
                  color="primary"
                  class="mr-3"
                />
                <v-btn
                  small
                  color="primary"
                  :loading="savingOrder"
                  :disabled="!orderDirty || flatLoading"
                  @click="saveOrder"
                >
                  <v-icon left small>mdi-content-save</v-icon>순서 저장
                </v-btn>
              </v-toolbar>
              <v-divider />

              <v-alert v-if="orderDirty" dense text type="warning" class="ma-2 mb-0 text-caption py-1">
                순서가 바뀌었습니다. <strong>'순서 저장'</strong> 을 눌러야 반영됩니다.
              </v-alert>
              <div v-else class="text-caption grey--text px-4 py-2 d-flex align-center">
                <v-icon x-small class="mr-1">mdi-drag-vertical</v-icon>
                손잡이를 잡고 드래그해서 노출 순서를 바꾸세요.
              </div>

              <div v-if="flatProducts.length > 0" class="scroll-pane-tall">
                <!-- 노출 상품: 드래그(⣿ 핸들)로 순서 변경 -->
                <draggable
                  v-model="visibleList"
                  handle=".drag-handle"
                  ghost-class="drag-ghost"
                  :animation="160"
                  @start="dragging = true"
                  @end="dragging = false"
                >
                  <v-list-item
                    v-for="(prd, idx) in visibleList"
                    :key="'vis-' + prd.PRODUCT_PID"
                    class="fp-row"
                  >
                    <v-list-item-action class="mr-1 my-0 drag-handle" title="드래그해서 순서 변경">
                      <v-icon color="grey">mdi-drag-vertical</v-icon>
                    </v-list-item-action>
                    <v-list-item-action class="mr-3 my-0">
                      <v-checkbox
                        :input-value="true"
                        :disabled="!!productBusy[prd.PRODUCT_PID]"
                        hide-details
                        class="mt-0 pt-0"
                        @change="toggleProductFlat(prd, $event)"
                      />
                    </v-list-item-action>
                    <v-list-item-avatar tile size="36" color="grey lighten-3">
                      <img v-if="prd.GOODS_IMAGE" :src="prd.GOODS_IMAGE" alt="상품" style="object-fit: contain;" />
                      <v-icon v-else small color="grey lighten-1">mdi-image-off-outline</v-icon>
                    </v-list-item-avatar>
                    <v-list-item-content>
                      <v-list-item-title class="text-body-2">{{ prd.PRODUCT_NAME }}</v-list-item-title>
                      <v-list-item-subtitle class="text-caption">
                        <span>{{ formatPrice(prd.SALE_PRICE) }}원</span>
                        <span class="grey--text ml-2">· {{ prd.CATEGORY_NAME }}</span>
                        <span v-if="prd.BRAND_NAME" class="grey--text ml-1">· {{ prd.BRAND_NAME }}</span>
                      </v-list-item-subtitle>
                    </v-list-item-content>
                    <v-list-item-action>
                      <v-chip x-small color="light-blue lighten-4" class="font-weight-medium">#{{ idx + 1 }}</v-chip>
                    </v-list-item-action>
                  </v-list-item>
                </draggable>

                <!-- 숨김·비활성: 자동 하단(드래그 불가). 체크하면 노출로 올라감 -->
                <template v-if="hiddenList.length > 0">
                  <div class="text-caption grey--text px-4 py-2 grey lighten-4 d-flex align-center">
                    <v-icon x-small class="mr-1">mdi-eye-off-outline</v-icon>
                    숨김·비활성 ({{ hiddenList.length }}) — 체크하면 노출됩니다
                  </div>
                  <v-divider />
                  <v-list-item
                    v-for="prd in hiddenList"
                    :key="'hid-' + prd.PRODUCT_PID"
                    class="grey lighten-5"
                  >
                    <v-list-item-action class="mr-1 my-0" style="min-width: 24px;"></v-list-item-action>
                    <v-list-item-action class="mr-3 my-0">
                      <v-checkbox
                        :input-value="false"
                        :disabled="prd.ACTIVE === 'N' || prd.CAT_ENABLED !== 'Y' || !!productBusy[prd.PRODUCT_PID]"
                        hide-details
                        class="mt-0 pt-0"
                        @change="toggleProductFlat(prd, $event)"
                      />
                    </v-list-item-action>
                    <v-list-item-avatar tile size="36" color="grey lighten-3">
                      <img v-if="prd.GOODS_IMAGE" :src="prd.GOODS_IMAGE" alt="상품" style="object-fit: contain; opacity: 0.55;" />
                      <v-icon v-else small color="grey lighten-1">mdi-image-off-outline</v-icon>
                    </v-list-item-avatar>
                    <v-list-item-content>
                      <v-list-item-title class="text-body-2 grey--text">{{ prd.PRODUCT_NAME }}</v-list-item-title>
                      <v-list-item-subtitle class="text-caption">
                        <span>{{ formatPrice(prd.SALE_PRICE) }}원</span>
                        <span class="grey--text ml-2">· {{ prd.CATEGORY_NAME }}</span>
                        <span v-if="prd.ACTIVE === 'N'" class="red--text ml-2">· 상품 비활성(검수 전)</span>
                        <span v-else-if="prd.CAT_ENABLED !== 'Y'" class="orange--text text--darken-2 ml-2">· 카테고리 숨김</span>
                        <span v-else class="grey--text ml-2">· 숨김</span>
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </template>
              </div>

              <v-container v-else-if="!flatLoading" class="text-center py-8">
                <v-icon size="48" color="grey lighten-1">mdi-package-variant</v-icon>
                <p class="text-body-2 grey--text mt-3 mb-0">상품이 없습니다.</p>
              </v-container>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- ================= 노출 미리보기 (모바일 스토어 레이아웃) ================= -->
    <v-dialog v-model="previewDialog" max-width="460" scrollable>
      <v-card class="preview-phone">
        <!-- 헤더 -->
        <v-toolbar flat dense class="preview-header">
          <v-btn icon @click="previewDialog = false"><v-icon>mdi-arrow-left</v-icon></v-btn>
          <v-toolbar-title class="text-subtitle-1 font-weight-bold">
            {{ (selectedPlacement && (selectedPlacement.NAME || selectedPlacement.CODE)) || '미리보기' }}
          </v-toolbar-title>
          <v-spacer />
          <v-chip x-small color="grey lighten-3" class="font-weight-medium">{{ previewItems.length }}개</v-chip>
        </v-toolbar>
        <v-divider />

        <!-- 정렬 (추후 SORT_ORDER 도입 대비) -->
        <div class="d-flex align-center px-3 preview-sortbar">
          <v-icon small class="mr-1 grey--text text--darken-1">mdi-sort</v-icon>
          <v-select
            v-model="previewSort"
            :items="previewSortItems"
            dense
            hide-details
            solo
            flat
            background-color="transparent"
            class="preview-sort-select"
            style="max-width: 220px"
          />
        </div>
        <v-divider />

        <v-card-text class="pa-0 preview-body">
          <v-overlay :value="previewLoading" absolute opacity="0.55">
            <v-progress-circular indeterminate size="40" />
          </v-overlay>

          <template v-if="sortedPreviewItems.length > 0">
            <div
              v-for="it in sortedPreviewItems"
              :key="'pv-' + it.product_pid"
              class="preview-row"
            >
              <div class="preview-thumb">
                <img v-if="it.image" :src="it.image" alt="상품" />
                <v-icon v-else color="grey lighten-1" size="26">mdi-image-off-outline</v-icon>
              </div>
              <div class="preview-info">
                <div class="preview-name">{{ it.name }}</div>
                <div class="preview-sub">{{ it.brand || it.category || '-' }}</div>
              </div>
              <div class="preview-value">{{ formatPrice(it.sale_price) }}원</div>
            </div>
            <div class="py-2"></div>
          </template>

          <v-container v-else-if="!previewLoading" class="text-center py-10">
            <v-icon size="56" color="grey lighten-1">mdi-eye-off-outline</v-icon>
            <p class="text-body-2 grey--text mt-3 mb-1">노출될 상품이 없습니다.</p>
            <p class="text-caption grey--text mb-0">
              상품 검수(ACTIVE=Y)·카테고리/상품 노출 설정을 확인하세요.
            </p>
          </v-container>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- ================= 새 플레이스먼트 다이얼로그 ================= -->
    <v-dialog v-model="createDialog" max-width="560" persistent>
      <v-card>
        <v-card-title class="text-subtitle-1">새 플레이스먼트</v-card-title>
        <v-divider />
        <v-card-text class="pt-4">
          <v-text-field
            v-model="createForm.code"
            label="코드 (영문/숫자, 고유키)"
            placeholder="MAIN_SHOP"
            outlined
            dense
            hide-details
            class="mb-3"
          />
          <v-text-field
            v-model="createForm.name"
            label="이름 (표시용)"
            placeholder="메인 상점"
            outlined
            dense
            hide-details
            class="mb-3"
          />
          <v-select
            v-model="createForm.new_category_default"
            :items="ynItems"
            label="새 카테고리 기본노출"
            outlined
            dense
            hide-details
            class="mb-3"
          />
          <v-select
            v-model="createForm.new_product_default"
            :items="ynItems"
            label="새 상품 기본노출"
            outlined
            dense
            hide-details
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="creating" @click="createDialog = false">취소</v-btn>
          <v-btn color="primary" :loading="creating" @click="createPlacement">생성</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { coupon } from '../../api';
import draggable from 'vuedraggable';

export default {
  name: 'Coupon_Placements',
  components: { draggable },
  data() {
    return {
      loading: false,
      productLoading: false,
      basicSaving: false,
      creating: false,

      placements: [],
      placementHeaders: [
        { text: 'CODE', value: 'CODE', width: '180' },
        { text: '이름', value: 'NAME' },
        { text: '활성', value: 'ACTIVE', width: '100', align: 'center' },
        { text: '캐시버전', value: 'CACHE_VERSION', width: '110', align: 'center' },
        { text: '최종생성', value: 'CACHE_GENERATED_AT', width: '180' },
      ],

      selectedPlacement: null,
      categories: [],
      products: [],
      selectedCategoryPid: null,

      basic: {
        active: false,
        newCategoryDefault: false,
        newProductDefault: false,
      },

      categoryBusy: {},
      productBusy: {},

      // 상품 노출·정렬 (평면 전체) — 활성 상위 / 비활성·숨김 하단 자동
      flatProducts: [],
      flatLoading: false,
      orderDirty: false,
      savingOrder: false,
      dragging: false,

      // 노출 미리보기 (모바일 스토어 레이아웃)
      previewDialog: false,
      previewLoading: false,
      previewItems: [],
      previewSort: 'default',
      previewSortItems: [
        { text: '기본 정렬 (카테고리·가격)', value: 'default' },
        { text: '가격 낮은순', value: 'price_asc' },
        { text: '가격 높은순', value: 'price_desc' },
        { text: '이름순 (가나다)', value: 'name_asc' },
        { text: '브랜드순', value: 'brand_asc' },
      ],

      createDialog: false,
      createForm: {
        code: '',
        name: '',
        new_category_default: 'Y',
        new_product_default: 'Y',
      },
      ynItems: [
        { text: '노출 (Y)', value: 'Y' },
        { text: '숨김 (N)', value: 'N' },
      ],
    };
  },
  computed: {
    selectedCategory() {
      if (!this.selectedCategoryPid) return null;
      return this.categories.find((c) => c.PID === this.selectedCategoryPid) || null;
    },
    // 평면 리스트에서 실제 노출되는 상품 수 (상위 그룹)
    visibleCount() {
      return this.flatProducts.filter((p) => p.EFFECTIVE_ENABLED === 'Y').length;
    },
    // 노출 상품 — 드래그로 순서 변경. set 시 숨김목록과 다시 합쳐 flatProducts 갱신.
    visibleList: {
      get() {
        return this.flatProducts.filter((p) => p.EFFECTIVE_ENABLED === 'Y');
      },
      set(newVisible) {
        const hidden = this.flatProducts.filter((p) => p.EFFECTIVE_ENABLED !== 'Y');
        this.flatProducts = newVisible.concat(hidden);
        this.orderDirty = true;
      },
    },
    // 숨김·비활성 상품 — 하단 고정(드래그 불가)
    hiddenList() {
      return this.flatProducts.filter((p) => p.EFFECTIVE_ENABLED !== 'Y');
    },
    // 미리보기 정렬 (기본순은 서버가 준 카테고리·가격 순서 유지)
    sortedPreviewItems() {
      const items = this.previewItems.slice();
      switch (this.previewSort) {
        case 'price_asc':  return items.sort((a, b) => a.sale_price - b.sale_price);
        case 'price_desc': return items.sort((a, b) => b.sale_price - a.sale_price);
        case 'name_asc':   return items.sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'ko'));
        case 'brand_asc':  return items.sort((a, b) => String(a.brand || '').localeCompare(String(b.brand || ''), 'ko'));
        default:           return items;
      }
    },
  },
  mounted() {
    this.loadPlacements();
  },
  methods: {
    // ---------- 공통 ----------
    handleError(e, fallback) {
      alert(typeof e === 'string' ? e : (fallback || '처리에 실패했습니다.'));
    },
    formatDate(v) {
      if (!v) return '-';
      const d = new Date(v);
      if (isNaN(d.getTime())) return String(v);
      const p = (n) => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
    },
    formatPrice(v) {
      const n = Number(v);
      if (isNaN(n)) return '-';
      return n.toLocaleString();
    },

    // ---------- 1단 ----------
    loadPlacements() {
      this.loading = true;
      coupon.getPlacements()
        .then((res) => {
          this.placements = (res && res.placements) || [];
        })
        .catch((e) => { this.handleError(e, '플레이스먼트 목록을 불러오지 못했습니다.'); })
        .finally(() => { this.loading = false; });
    },
    openCreateDialog() {
      this.createForm = {
        code: '',
        name: '',
        new_category_default: 'Y',
        new_product_default: 'Y',
      };
      this.createDialog = true;
    },
    createPlacement() {
      const code = (this.createForm.code || '').trim();
      const name = (this.createForm.name || '').trim();
      if (!code) {
        alert('코드를 입력해주세요.');
        return;
      }
      if (!name) {
        alert('이름을 입력해주세요.');
        return;
      }
      this.creating = true;
      coupon.createPlacement({
        code,
        name,
        new_category_default: this.createForm.new_category_default,
        new_product_default: this.createForm.new_product_default,
      })
        .then(() => {
          this.createDialog = false;
          this.loadPlacements();
        })
        .catch((e) => { this.handleError(e, '플레이스먼트 생성에 실패했습니다.'); })
        .finally(() => { this.creating = false; });
    },

    // ---------- 2단 진입 / 이탈 ----------
    openPlacement(item) {
      if (!item) return;
      this.selectedPlacement = item;
      this.selectedCategoryPid = null;
      this.categories = [];
      this.products = [];
      this.flatProducts = [];
      this.orderDirty = false;
      this.categoryBusy = {};
      this.productBusy = {};
      this.syncBasicFrom(item);
      this.loadDetail(null);
      this.loadFlatProducts();
    },
    backToList() {
      this.selectedPlacement = null;
      this.selectedCategoryPid = null;
      this.categories = [];
      this.products = [];
      this.categoryBusy = {};
      this.productBusy = {};
      this.loadPlacements();
    },
    syncBasicFrom(p) {
      this.basic = {
        active: p.ACTIVE === 'Y',
        newCategoryDefault: p.NEW_CATEGORY_DEFAULT === 'Y',
        newProductDefault: p.NEW_PRODUCT_DEFAULT === 'Y',
      };
    },

    // ---------- 상세 조회 ----------
    loadDetail(categoryPid) {
      if (!this.selectedPlacement) return Promise.resolve();
      const pid = this.selectedPlacement.PID;
      this.loading = true;
      return coupon.getPlacementDetail(pid, categoryPid || undefined)
        .then((res) => {
          if (!res) return;
          if (res.placement) {
            this.selectedPlacement = res.placement;
            this.syncBasicFrom(res.placement);
          }
          this.categories = res.categories || [];
          this.products = res.products || [];
        })
        .catch((e) => { this.handleError(e, '상세 정보를 불러오지 못했습니다.'); })
        .finally(() => { this.loading = false; });
    },
    reloadDetail() {
      this.loadDetail(this.selectedCategoryPid);
      this.loadFlatProducts();
    },

    // ---------- 노출 미리보기 ----------
    // 현재 설정 그대로(크론 대기 없이) 게임서버에 내려갈 상품을 실시간 계산해 보여준다.
    openPreview() {
      if (!this.selectedPlacement) return;
      this.previewDialog = true;
      this.previewLoading = true;
      this.previewItems = [];
      coupon.previewPlacement(this.selectedPlacement.PID)
        .then((res) => {
          this.previewItems = (res && res.products) || [];
        })
        .catch((e) => { this.handleError(e, '미리보기를 불러오지 못했습니다.'); })
        .finally(() => { this.previewLoading = false; });
    },

    // ---------- 상품 노출·정렬 (평면 전체) ----------
    loadFlatProducts() {
      if (!this.selectedPlacement) return;
      this.flatLoading = true;
      this.orderDirty = false;
      coupon.getPlacementProducts(this.selectedPlacement.PID)
        .then((res) => { this.flatProducts = (res && res.products) || []; })
        .catch((e) => { this.handleError(e, '상품 목록을 불러오지 못했습니다.'); })
        .finally(() => { this.flatLoading = false; });
    },
    // 노출 상품끼리만 인접 이동(노출 상품은 상단에 연속 배치됨)
    canMoveUp(idx) {
      const cur = this.flatProducts[idx];
      const prev = this.flatProducts[idx - 1];
      return !!cur && cur.EFFECTIVE_ENABLED === 'Y' && !!prev && prev.EFFECTIVE_ENABLED === 'Y';
    },
    canMoveDown(idx) {
      const cur = this.flatProducts[idx];
      const next = this.flatProducts[idx + 1];
      return !!cur && cur.EFFECTIVE_ENABLED === 'Y' && !!next && next.EFFECTIVE_ENABLED === 'Y';
    },
    moveUp(idx) {
      if (!this.canMoveUp(idx)) return;
      const arr = this.flatProducts.slice();
      const t = arr[idx - 1]; arr[idx - 1] = arr[idx]; arr[idx] = t;
      this.flatProducts = arr;
      this.orderDirty = true;
    },
    moveDown(idx) {
      if (!this.canMoveDown(idx)) return;
      const arr = this.flatProducts.slice();
      const t = arr[idx + 1]; arr[idx + 1] = arr[idx]; arr[idx] = t;
      this.flatProducts = arr;
      this.orderDirty = true;
    },
    // 노출 상품은 상단, 비활성·숨김은 하단으로 (각 그룹 내 상대순서 유지)
    resortFlat() {
      const vis = this.flatProducts.filter((p) => p.EFFECTIVE_ENABLED === 'Y');
      const hid = this.flatProducts.filter((p) => p.EFFECTIVE_ENABLED !== 'Y');
      this.flatProducts = vis.concat(hid);
    },
    toggleProductFlat(prd, checked) {
      const next = checked ? 'Y' : 'N';
      this.$set(this.productBusy, prd.PRODUCT_PID, true);
      coupon.setPlacementProduct(this.selectedPlacement.PID, {
        product_pid: prd.PRODUCT_PID,
        state: next,
      })
        .then(() => {
          this.$set(prd, 'OVERRIDE_STATE', next);
          const eff = (prd.ACTIVE === 'Y' && prd.CAT_ENABLED === 'Y' && next === 'Y') ? 'Y' : 'N';
          this.$set(prd, 'EFFECTIVE_ENABLED', eff);
          this.resortFlat();
        })
        .catch((e) => { this.handleError(e, '상품 노출 설정에 실패했습니다.'); })
        .finally(() => { this.$set(this.productBusy, prd.PRODUCT_PID, false); });
    },
    saveOrder() {
      if (!this.selectedPlacement) return;
      const ordered = this.flatProducts
        .filter((p) => p.EFFECTIVE_ENABLED === 'Y')
        .map((p) => p.PRODUCT_PID);
      this.savingOrder = true;
      coupon.savePlacementProductOrder(this.selectedPlacement.PID, ordered)
        .then(() => {
          this.orderDirty = false;
          if (this.selectedPlacement) {
            this.selectedPlacement.CACHE_VERSION = (Number(this.selectedPlacement.CACHE_VERSION) || 0) + 1;
          }
        })
        .catch((e) => { this.handleError(e, '순서 저장에 실패했습니다.'); })
        .finally(() => { this.savingOrder = false; });
    },

    // ---------- 기본 카드 ----------
    onBasicChange(field, value) {
      const prev = !value;
      this.basicSaving = true;
      coupon.updatePlacement(this.selectedPlacement.PID, {
        name: this.selectedPlacement.NAME,
        active: this.basic.active ? 'Y' : 'N',
        new_category_default: this.basic.newCategoryDefault ? 'Y' : 'N',
        new_product_default: this.basic.newProductDefault ? 'Y' : 'N',
      })
        .then(() => {
          // 서버 반영값을 로컬 원본에도 동기화
          this.selectedPlacement.ACTIVE = this.basic.active ? 'Y' : 'N';
          this.selectedPlacement.NEW_CATEGORY_DEFAULT = this.basic.newCategoryDefault ? 'Y' : 'N';
          this.selectedPlacement.NEW_PRODUCT_DEFAULT = this.basic.newProductDefault ? 'Y' : 'N';
          // 기본값이 바뀌면 실효값(EFFECTIVE_ENABLED)이 달라지므로 재조회
          if (field !== 'active') {
            this.loadDetail(this.selectedCategoryPid);
            this.loadFlatProducts();
          }
        })
        .catch((e) => {
          this.basic[field] = prev; // 롤백
          this.handleError(e, '설정 저장에 실패했습니다.');
        })
        .finally(() => { this.basicSaving = false; });
    },

    // ---------- 카테고리 ----------
    selectCategory(cat) {
      this.selectedCategoryPid = cat.PID;
      this.loadProducts(cat.PID);
    },
    loadProducts(categoryPid) {
      if (!this.selectedPlacement) return;
      this.productLoading = true;
      coupon.getPlacementDetail(this.selectedPlacement.PID, categoryPid)
        .then((res) => {
          this.products = (res && res.products) || [];
        })
        .catch((e) => {
          this.products = [];
          this.handleError(e, '상품 목록을 불러오지 못했습니다.');
        })
        .finally(() => { this.productLoading = false; });
    },
    toggleCategory(cat, checked) {
      const next = checked ? 'Y' : 'N';
      const prevEffective = cat.EFFECTIVE_ENABLED;
      const prevOverride = cat.OVERRIDE_ENABLED;
      // 낙관적 업데이트
      this.$set(cat, 'EFFECTIVE_ENABLED', next);
      this.$set(cat, 'OVERRIDE_ENABLED', next);
      this.$set(this.categoryBusy, cat.PID, true);

      coupon.setPlacementCategory(this.selectedPlacement.PID, {
        category_pid: cat.PID,
        enabled: next,
      })
        .then(() => {
          // 카테고리 노출이 바뀌면 하위 상품 실효값에도 영향을 주므로 평면 리스트 재조회
          this.loadFlatProducts();
        })
        .catch((e) => {
          this.$set(cat, 'EFFECTIVE_ENABLED', prevEffective);
          this.$set(cat, 'OVERRIDE_ENABLED', prevOverride);
          this.handleError(e, '카테고리 노출 설정에 실패했습니다.');
        })
        .finally(() => { this.$set(this.categoryBusy, cat.PID, false); });
    },

    // ---------- 상품 ----------
    toggleProduct(prd, checked) {
      const next = checked ? 'Y' : 'N';
      const prevEffective = prd.EFFECTIVE_ENABLED;
      const prevOverride = prd.OVERRIDE_STATE;
      this.$set(prd, 'EFFECTIVE_ENABLED', next);
      this.$set(prd, 'OVERRIDE_STATE', next);
      this.$set(this.productBusy, prd.PID, true);

      coupon.setPlacementProduct(this.selectedPlacement.PID, {
        product_pid: prd.PID,
        state: next,
      })
        .catch((e) => {
          this.$set(prd, 'EFFECTIVE_ENABLED', prevEffective);
          this.$set(prd, 'OVERRIDE_STATE', prevOverride);
          this.handleError(e, '상품 노출 설정에 실패했습니다.');
        })
        .finally(() => { this.$set(this.productBusy, prd.PID, false); });
    },
  },
};
</script>

<style scoped>
.row-pointer >>> tbody tr {
  cursor: pointer;
}
.scroll-pane {
  max-height: 520px;
  overflow-y: auto;
}
.scroll-pane-tall {
  max-height: 620px;
  overflow-y: auto;
}
/* 드래그 정렬 */
.drag-handle { cursor: grab; }
.drag-handle:active { cursor: grabbing; }
.fp-row { background: #ffffff; }
.drag-ghost {
  opacity: 0.6;
  background: #e3f2fd !important;
}

/* ===== 노출 미리보기 - 모바일 스토어 레이아웃 (첨부 이미지 스타일) ===== */
.preview-phone {
  border-radius: 16px;
  overflow: hidden;
}
.preview-header {
  background: #ffffff !important;
}
.preview-sortbar {
  background: #fafbfc;
  min-height: 44px;
}
.preview-sort-select >>> .v-input__slot {
  min-height: 32px !important;
}
.preview-body {
  background: #f4f6f8;
  min-height: 220px;
  max-height: 62vh;
  overflow-y: auto;
}
.preview-row {
  display: flex;
  align-items: center;
  background: #ffffff;
  margin: 8px 10px;
  padding: 10px 12px;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}
.preview-thumb {
  width: 52px;
  height: 52px;
  border-radius: 10px;
  background: #f2f4f7;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex: 0 0 auto;
}
.preview-thumb img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}
.preview-info {
  flex: 1 1 auto;
  min-width: 0;
  padding: 0 12px;
}
.preview-name {
  font-size: 0.95rem;
  font-weight: 600;
  color: #222;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.preview-sub {
  font-size: 0.8rem;
  color: #8a94a6;
  margin-top: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.preview-value {
  flex: 0 0 auto;
  background: #4a7dff;
  color: #ffffff;
  font-size: 0.85rem;
  font-weight: 700;
  padding: 6px 14px;
  border-radius: 999px;
  white-space: nowrap;
}
</style>
